import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/PageHeader";
import AuthImage from "@/components/AuthImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Users, ClipboardList, Wallet, ShieldAlert, ScrollText, Plus, Trash2, Loader2,
  FileDown, UserPlus, Ban, CircleCheck, TrendingUp, TrendingDown, Search, Shield, Upload, Image as ImageIcon,
} from "lucide-react";
import { KeyRound, Flag } from "lucide-react";
import InvitesTab from "@/components/command/InvitesTab";
import ReportsTab from "@/components/command/ReportsTab";
import { useBrandLogo, refreshBrandLogo } from "@/components/Logo";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const ngn = (n) => "$" + Number(n || 0).toLocaleString();
const roleLabel = { super_admin: "Super Admin", officer: "Officer", member: "Member" };
const PIE = ["#D4AF37", "#0EA5E9", "#10B981", "#EF4444", "#8B5CF6", "#F59E0B"];
const FRIGATES = ["Cherokee", "Cumberland", "Pelican"];
const POSITIONS = ["Member", "Frigate Captain", "GM", "Penmate", "Mascot", "Crier", "Scribe", "Purse", "Galleon Purse", "FC", "Visiting Brother"];
const GALLEON_POSITIONS = ["Galleon Purse", "FC", "Visiting Brother"];

/* ---------------- Members ---------------- */
function MembersTab() {
  const { user: me, hasRole } = useAuth();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/members", { params: { q } }).then((r) => setMembers(r.data.members));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openAdd = () => { setEditing(null); setForm({ name: "", email: "", deck_name: "", position: "Member", frigate: "", phone: "", birthday: "", bio: "" }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m, frigate: m.frigate || "" }); setOpen(true); };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/members/${editing.id}`, {
          name: form.name, deck_name: form.deck_name, position: form.position, frigate: form.frigate,
          phone: form.phone, birthday: form.birthday, bio: form.bio,
        });
        toast.success("Member updated");
      } else {
        const { data } = await api.post("/admin/members", { ...form, frigate: form.frigate || null });
        toast.success(`Member added. Temp password: ${data.temp_password}`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const suspend = async (m) => { try { const { data } = await api.post(`/admin/members/${m.id}/suspend`); toast.success(data.status === "suspended" ? `${m.name} suspended` : `${m.name} is active again`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resetPhrase = async (m) => {
    if (!window.confirm(`Reset ${m.name}'s security phrase? He will set a new one at his next sign-in.`)) return;
    try { await api.post(`/admin/members/${m.id}/reset-phrase`); toast.success(`${m.name}'s security phrase was reset`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async (m) => { try { await api.post(`/admin/members/${m.id}/restore`); toast.success(`${m.name} is back aboard`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.name}?`)) return; await api.delete(`/admin/members/${m.id}`); toast.success("Member removed"); load(); };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="member-search-input" />
        </div>
        <Button onClick={openAdd} className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="add-member-button"><UserPlus className="mr-2 h-4 w-4" />Add Member</Button>
      </div>
      <Card className="gold-ring glass overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Member</TableHead><TableHead>Position</TableHead><TableHead className="hidden md:table-cell">Frigate</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      {m.photo_path ? <AuthImage path={m.photo_path} className="h-8 w-8 rounded-full object-cover" />
                        : <AvatarFallback className="bg-secondary text-xs">{m.name[0]}</AvatarFallback>}
                    </Avatar>
                    <div><p className="text-sm font-600">{m.name}{m.deck_name ? <span className="text-muted-foreground"> · "{m.deck_name}"</span> : null}</p><p className="text-xs text-muted-foreground">{m.email}</p></div>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className="border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))]">{m.position}</Badge></TableCell>
                <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{m.frigate || "Galleon"}</TableCell>
                <TableCell>
                  {m.status === "suspended" ? <Badge variant="destructive">Suspended</Badge> : m.status === "left" ? <Badge variant="outline" className="border-amber-500/50 text-amber-500">Left</Badge> : <Badge className="bg-emerald-500/20 text-emerald-500">Active</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {m.status === "left" ? (
                    <Button size="sm" variant="outline" className="gold-ring" onClick={() => restore(m)} data-testid={`restore-member-${m.id}`}>Restore</Button>
                  ) : (<>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>Edit</Button>
                  {m.has_security_phrase && m.id !== me?.id && m.role !== "super_admin" && (
                    <Button size="icon" variant="ghost" title="Reset security phrase" onClick={() => resetPhrase(m)} className="text-[hsl(var(--gold))]" data-testid={`reset-phrase-${m.id}`}><KeyRound className="h-4 w-4" /></Button>
                  )}
                  {m.id !== me?.id && m.role !== "super_admin" && (m.status === "suspended"
                    ? <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500 hover:text-white" onClick={() => suspend(m)} data-testid={`unsuspend-member-${m.id}`}><CircleCheck className="mr-1 h-4 w-4" />Unsuspend</Button>
                    : <Button size="icon" variant="ghost" title="Suspend" onClick={() => suspend(m)} className="text-amber-500" data-testid={`suspend-member-${m.id}`}><Ban className="h-4 w-4" /></Button>)}
                  {hasRole("super_admin") && m.id !== me?.id && <Button size="icon" variant="ghost" title="Remove" onClick={() => remove(m)} className="text-destructive" data-testid={`remove-member-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </>)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Member" : "Add Member"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="form-member-name" /></div>
            <div><Label>Email</Label><Input type="email" disabled={!!editing} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="form-member-email" /></div>
            <div><Label>Deck Name</Label><Input value={form.deck_name || ""} onChange={(e) => setForm({ ...form, deck_name: e.target.value })} className="mt-1" data-testid="form-member-deck" placeholder="e.g. Stormrider" /></div>
            <div>
              <Label>Frigate</Label>
              <Select value={form.frigate || "none"} onValueChange={(v) => setForm({ ...form, frigate: v === "none" ? "" : v })} disabled={!hasRole("super_admin") && !me?.is_galleon_admin}>
                <SelectTrigger className="mt-1" data-testid="form-member-frigate"><SelectValue placeholder="Frigate" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Galleon (none)</SelectItem>
                  {FRIGATES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Officer Position</Label>
              <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                <SelectTrigger className="mt-1" data-testid="form-member-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((p) => (
                    <SelectItem key={p} value={p} disabled={GALLEON_POSITIONS.includes(p) && !me?.is_galleon_admin}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Birthday (MM-DD)</Label><Input value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} placeholder="07-15" className="mt-1" /></div>
          </div>
          {!editing && <p className="text-xs text-muted-foreground">A temporary password (Vessel@2026) will be assigned unless specified.</p>}
          <DialogFooter><Button onClick={submit} disabled={busy} className="bg-[hsl(var(--gold))] text-[#0A192F]" data-testid="save-member-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Tasks ---------------- */
const COLS = [{ key: "todo", label: "To Do" }, { key: "in_progress", label: "In Progress" }, { key: "done", label: "Done" }];
const PRIO = { high: "bg-rose-500/20 text-rose-500", medium: "bg-amber-500/20 text-amber-500", low: "bg-sky-500/20 text-sky-500" };

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", deadline: "", priority: "medium" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tasks").then((r) => setTasks(r.data.tasks));
  useEffect(() => {
    load();
    api.get("/members").then((r) => setOfficers(r.data.members.filter((m) => m.role !== "member")));
  }, []);

  const create = async () => {
    if (!form.title || !form.assignee_id) { toast.error("Title and assignee required"); return; }
    setBusy(true);
    try { await api.post("/tasks", form); toast.success("Task assigned"); setOpen(false); setForm({ title: "", description: "", assignee_id: "", deadline: "", priority: "medium" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const move = async (t, status) => { await api.put(`/tasks/${t.id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="officer-task-add-button"><Plus className="mr-2 h-4 w-4" />Assign Task</Button></DialogTrigger>
          <DialogContent className="glass">
            <DialogHeader><DialogTitle className="font-display">Assign Task</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="task-title-input" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Assign To</Label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="task-assignee-select"><SelectValue placeholder="Select officer" /></SelectTrigger>
                  <SelectContent>{officers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {roleLabel[o.role]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1" /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy} className="bg-[hsl(var(--gold))] text-[#0A192F]" data-testid="task-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {COLS.map((col) => (
          <div key={col.key} data-testid={`task-col-${col.key}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-sm font-700 uppercase tracking-wider text-muted-foreground">{col.label}</h3>
              <Badge variant="outline">{tasks.filter((t) => t.status === col.key).length}</Badge>
            </div>
            <div className="space-y-3">
              {tasks.filter((t) => t.status === col.key).map((t) => (
                <Card key={t.id} className="gold-ring glass p-4" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <Badge className={PRIO[t.priority]}>{t.priority}</Badge>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => del(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <p className="mt-2 text-sm font-600">{t.title}</p>
                  {t.description && <p className="mt-1 text-xs text-muted-foreground">{t.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{t.assignee_name}</span>
                    {t.deadline && <span className="font-mono-data text-[0.65rem] text-muted-foreground">{t.deadline}</span>}
                  </div>
                  <div className="mt-3 flex gap-1">
                    {COLS.filter((c) => c.key !== t.status).map((c) => (
                      <Button key={c.key} size="sm" variant="outline" className="h-7 flex-1 text-[0.65rem] gold-ring" onClick={() => move(t, c.key)} data-testid={`move-${t.id}-${c.key}`}>
                        → {c.label}
                      </Button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Finance ---------------- */
function FinanceTab() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "income", category: "", amount: "", currency: "USD", note: "", entry_date: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/finance/entries").then((r) => setEntries(r.data.entries));
    api.get("/finance/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.category || !form.amount) { toast.error("Category and amount required"); return; }
    setBusy(true);
    try { await api.post("/finance/entries", { ...form, amount: parseFloat(form.amount) }); toast.success("Entry logged"); setOpen(false); setForm({ type: "income", category: "", amount: "", currency: "USD", note: "", entry_date: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/finance/entries/${id}`); load(); };

  return (
    <div>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="gold-ring glass p-5"><p className="label-eyebrow text-muted-foreground">Total Income</p><p className="mt-1 flex items-center gap-2 font-display text-2xl font-700 text-emerald-400"><TrendingUp className="h-5 w-5" />{ngn(summary?.income)}</p></Card>
        <Card className="gold-ring glass p-5"><p className="label-eyebrow text-muted-foreground">Total Expense</p><p className="mt-1 flex items-center gap-2 font-display text-2xl font-700 text-rose-400"><TrendingDown className="h-5 w-5" />{ngn(summary?.expense)}</p></Card>
        <Card className="gold-ring glass p-5"><p className="label-eyebrow text-muted-foreground">Net Balance</p><p className="mt-1 font-display text-2xl font-700 gold-text">{ngn(summary?.balance)}</p></Card>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="gold-ring glass p-5">
          <h3 className="mb-3 font-display text-sm font-700">Monthly Income vs Expense</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary?.monthly || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--gold))" }} formatter={(v) => ngn(v)} />
              <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="gold-ring glass p-5">
          <h3 className="mb-3 font-display text-sm font-700">Expense by Category</h3>
          <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={summary?.expense_by_category || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => e.name}>
                {(summary?.expense_by_category || []).map((c, i) => <Cell key={c.name || i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => ngn(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--gold))" }} />
            </PieChart>
          </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-700">Ledger</h3>
        <div className="flex gap-2">
          <Button variant="outline" className="gold-ring" onClick={() => downloadBlob("/finance/export", "Iota_Financial_Audit.pdf")} data-testid="financial-export-pdf-button"><FileDown className="mr-2 h-4 w-4" />Export PDF</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="add-finance-button"><Plus className="mr-2 h-4 w-4" />Add Entry</Button></DialogTrigger>
            <DialogContent className="glass">
              <DialogHeader><DialogTitle className="font-display">Log Financial Entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="finance-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="income">Income</SelectItem><SelectItem value="expense">Expense</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="e.g. Membership Dues" data-testid="finance-category-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (USD $)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="finance-amount-input" /></div>
                  <div><Label>Date</Label><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
              </div>
              <DialogFooter><Button onClick={add} disabled={busy} className="bg-[hsl(var(--gold))] text-[#0A192F]" data-testid="finance-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log Entry"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Card className="gold-ring glass overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id} data-testid={`finance-row-${e.id}`}>
                <TableCell className="font-mono-data text-xs">{e.entry_date}</TableCell>
                <TableCell>{e.type === "income" ? <Badge className="bg-emerald-500/20 text-emerald-500">Income</Badge> : <Badge className="bg-rose-500/20 text-rose-500">Expense</Badge>}</TableCell>
                <TableCell className="text-sm">{e.category}</TableCell>
                <TableCell className={`text-right font-mono-data ${e.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>{e.type === "income" ? "+" : "-"}{ngn(e.amount)}</TableCell>
                <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(e.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Feedback ---------------- */
function FeedbackTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/feedback").then((r) => setItems(r.data.feedback));
  useEffect(() => { load(); }, []);
  const setStatus = async (f, status) => { await api.put(`/feedback/${f.id}`, { status }); load(); };
  return (
    <div className="space-y-3">
      {items.map((f) => (
        <Card key={f.id} className="gold-ring glass p-5" data-testid={`feedback-item-${f.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]">{f.category}</Badge>
              <p className="mt-2 text-sm">{f.message}</p>
            </div>
            <Select value={f.status} onValueChange={(v) => setStatus(f, v)}>
              <SelectTrigger className="w-[130px]" data-testid={`feedback-status-${f.id}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="reviewing">Reviewing</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent>
            </Select>
          </div>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-muted-foreground">No feedback submitted yet.</p>}
    </div>
  );
}

/* ---------------- Audit Log ---------------- */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/audit", { params: { q } }).then((r) => setLogs(r.data.logs));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search audit trail…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="audit-search-input" />
        </div>
        <Button variant="outline" className="gold-ring" onClick={() => downloadBlob("/reports/engagement", "Iota_Engagement_Report.pdf")} data-testid="export-engagement-button"><FileDown className="mr-2 h-4 w-4" />Engagement Report</Button>
      </div>
      <Card className="gold-ring glass overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead className="hidden md:table-cell">Detail</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                <TableCell className="font-mono-data text-[0.7rem] text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{l.user_name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono-data text-[0.65rem]">{l.action}</Badge></TableCell>
                <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{l.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Shell ---------------- */
/* ---------------- Branding ---------------- */
function BrandingTab() {
  const logo = useBrandLogo();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshBrandLogo(); setPreview(URL.createObjectURL(file));
      toast.success("Galleon logo updated — reload to see it everywhere.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const shown = preview || logo;
  return (
    <Card className="gold-ring glass max-w-xl p-6" data-testid="branding-tab">
      <h3 className="font-display text-lg font-700">IOTA Galleon Logo</h3>
      <p className="mt-1 text-sm text-muted-foreground">Shown on the login page, sidebar and header. PNG, JPG, WEBP or SVG; square works best.</p>
      <div className="mt-5 flex items-center gap-5">
        <div className="flex h-28 w-28 items-center justify-center rounded-2xl border border-[hsl(var(--gold))]/30 bg-background/60">
          {shown ? <img src={shown} alt="Galleon logo" className="h-24 w-24 object-contain" data-testid="branding-logo-preview" /> : <ImageIcon className="h-10 w-10 text-muted-foreground" />}
        </div>
        <label className="cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-file-input" disabled={busy} />
          <span className="inline-flex items-center rounded-md bg-[hsl(var(--gold))] px-4 py-2 text-sm font-700 text-[hsl(var(--primary-foreground))]">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{shown ? "Replace logo" : "Upload logo"}
          </span>
        </label>
      </div>
    </Card>
  );
}

export default function Command() {
  const { user } = useAuth();
  const galleonAdmin = !!user?.is_galleon_admin;
  return (
    <div>
      <PageHeader eyebrow="Restricted — Officers Only" title="Command Deck"
        description={galleonAdmin ? "Galleon-wide governance, membership, finances, and the perpetual audit trail." : `Frigate command for ${user?.frigate || "your frigate"} — members, tasks, and feedback.`} />
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "members"}>
        <TabsList className="glass mb-6 flex-wrap">
          <TabsTrigger value="members" data-testid="command-tab-members"><Users className="mr-1.5 h-4 w-4" />Members</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="command-tab-tasks"><ClipboardList className="mr-1.5 h-4 w-4" />Task Deck</TabsTrigger>
          <TabsTrigger value="invites" data-testid="command-tab-invites"><KeyRound className="mr-1.5 h-4 w-4" />Invites</TabsTrigger>
          {galleonAdmin && <TabsTrigger value="finance" data-testid="command-tab-finance"><Wallet className="mr-1.5 h-4 w-4" />Finance</TabsTrigger>}
          <TabsTrigger value="feedback" data-testid="command-tab-feedback"><ShieldAlert className="mr-1.5 h-4 w-4" />Feedback</TabsTrigger>
          <TabsTrigger value="reports" data-testid="command-tab-reports"><Flag className="mr-1.5 h-4 w-4" />Reports</TabsTrigger>
          {galleonAdmin && <TabsTrigger value="audit" data-testid="command-tab-audit"><ScrollText className="mr-1.5 h-4 w-4" />Audit Log</TabsTrigger>}
          {user?.role === "super_admin" && <TabsTrigger value="branding" data-testid="command-tab-branding"><ImageIcon className="mr-1.5 h-4 w-4" />Logo</TabsTrigger>}
        </TabsList>
        <TabsContent value="members"><MembersTab /></TabsContent>
        <TabsContent value="invites"><InvitesTab /></TabsContent>
        <TabsContent value="tasks"><TasksTab /></TabsContent>
        {galleonAdmin && <TabsContent value="finance"><FinanceTab /></TabsContent>}
        <TabsContent value="feedback"><FeedbackTab /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
        {galleonAdmin && <TabsContent value="audit"><AuditTab /></TabsContent>}
        {user?.role === "super_admin" && <TabsContent value="branding"><BrandingTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
